import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useMemo } from 'react';
import { FieldNamesMarkedBoolean, FormProvider, useForm } from 'react-hook-form';
import {
  isValidDomain,
  isValidHandlebar,
  isValidUrlWithHandlebarsAnywhere,
} from 'utils/validation';
import { z } from 'zod';
import {
  ACCEPTS_TYPES,
  FIELD_NAMES,
  MAX_IMAGE_SIZE,
  MAX_IMAGE_SIZE_IN_MB,
  SmsOptOutValues,
} from '../constants';
import { Project, ProjectLabel } from '../types';
import { createDefaultValues } from '../utils/defaultValues';

const {
  ADD_UNSUBSCRIBE,
  CUSTOM_UNSUBSCRIBE_SNIPPET,
  ENABLE_ANON_USER_TRACKING,
  ENABLE_EMAIL_CLICK_IP_UPDATE,
  ENABLE_GEO_IP_LOOKUP,
  ENABLE_SENDING_DOMAIN,
  FIRST_NAME_FIELD,
  LABELS,
  GROUP_RECURRING_CAMPAIGNS,
  HMAC_SECRET,
  HOSTED_UNSUBSCRIBE_URL,
  LAST_NAME_FIELD,
  LOCALES,
  MAPPED_LOCALES,
  LOGO_URL,
  NAME,
  OVERRIDE_CONTENT_NOT_FOUND_MARKETING,
  OVERRIDE_CONTENT_NOT_FOUND_TRANSACTIONAL,
  PHYSICAL_ADDRESS,
  SENDING_DOMAIN,
  SHOW_REVENUE,
  ENABLE_IMPROVED_TRACKING,
  SMS_OPT_OUT_INSTRUCTIONS,
  SMS_DISCLAIMER,
  SMS_SHORTLINKS_MIN_LENGTH_MARKETING,
  SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL,
  SUBSCRIBE_SUCCESS_URL,
  TEMPLATE_DEFAULT_LOCALE_MODIFIABLE,
  TIME_ZONE,
  USE_DOUBLE_OPT_OUT,
  USE_DYNAMIC_SENDERS,
  USE_HOSTED_UNSUBSCRIBE_URL_IN_LIST_UNSUBSCRIBE_HEADER,
  WYSIWYG_EDITOR_MODE,
  USE_FILTERED_TRACKING,
} = FIELD_NAMES;

const schema = z
  .object({
    /** Basic Section */
    /** =============== */
    [NAME]: z.string().min(1, { message: 'Name is required' }),
    [LOGO_URL]: z
      .string()
      .or(z.instanceof(File))
      .optional()
      .refine(
        (value) =>
          !value ||
          typeof value === 'string' ||
          (value?.type && Object.keys(ACCEPTS_TYPES).includes(value?.type)),
        'Invalid file type. Please upload a file as either a PNG, JPG, or GIF.',
      )
      .refine(
        (value) =>
          !value || typeof value === 'string' || (value?.size && value?.size < MAX_IMAGE_SIZE),
        `Invalid file size. Please upload a file under ${MAX_IMAGE_SIZE_IN_MB}MB`,
      ),
    [TIME_ZONE]: z.string(),
    [PHYSICAL_ADDRESS]: z.string().optional(),

    /** Messaging Section */
    /** ================= */
    [LABELS]: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
      }),
    ),
    [GROUP_RECURRING_CAMPAIGNS]: z.boolean(),

    /** Email Section - Sender Fields */
    /** ============================= */
    [CUSTOM_UNSUBSCRIBE_SNIPPET]: z.string().optional(),
    [FIRST_NAME_FIELD]: z.string().optional(),
    [LAST_NAME_FIELD]: z.string().optional(),
    [USE_DYNAMIC_SENDERS]: z.boolean(),
    [ENABLE_SENDING_DOMAIN]: z.boolean(),
    /** additional validation checks below, dependent on enableSendingDomain */
    [SENDING_DOMAIN]: z.string().optional(),

    /** Email Section - Subscriptions Fields */
    /** ==================================== */
    [ADD_UNSUBSCRIBE]: z.boolean(),
    [USE_DOUBLE_OPT_OUT]: z.boolean(),
    /** requires protocal, requires valid TLD and accepts handlebars */
    [HOSTED_UNSUBSCRIBE_URL]: z
      .string()
      .optional()
      .refine(
        (val) =>
          !val ||
          isValidUrlWithHandlebarsAnywhere(val).length < 1 ||
          isValidHandlebar(val).length < 1,
        'Please enter a valid URL.',
      ),
    [USE_HOSTED_UNSUBSCRIBE_URL_IN_LIST_UNSUBSCRIBE_HEADER]: z.boolean(),
    /** No BE validation for Subscribe Success URL, so we will not validate it here */
    [SUBSCRIBE_SUCCESS_URL]: z.string().optional(),

    /** SMS Section */
    /** =========== */
    [SMS_OPT_OUT_INSTRUCTIONS]: z.nativeEnum(SmsOptOutValues),
    [SMS_DISCLAIMER]: z.string().optional(),
    // SMS Shortlinks - return error message if min length is not between 5 and 20
    [SMS_SHORTLINKS_MIN_LENGTH_MARKETING]: z
      .number({ message: 'Please enter a value before proceeding.' })
      .min(5, { message: 'Please ensure the link has at least 5 characters.' })
      .max(20, { message: 'Please ensure the link is not over 20 characters.' }),
    [SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL]: z
      .number({ message: 'Please enter a value before proceeding.' })
      .min(5, { message: 'Please ensure the link has at least 5 characters.' })
      .max(20, { message: 'Please ensure the link is not over 20 characters.' }),

    /** Content Section */
    /** =============== */
    [WYSIWYG_EDITOR_MODE]: z.boolean(),
    [HMAC_SECRET]: z.string().optional(),
    [LOCALES]: z.array(z.string()).optional(),
    [MAPPED_LOCALES]: z
      .array(
        z.object({
          tag: z.string(),
          whatsAppInfobipLocale: z.string(),
        }),
      )
      .optional(),
    [TEMPLATE_DEFAULT_LOCALE_MODIFIABLE]: z.boolean(),
    [OVERRIDE_CONTENT_NOT_FOUND_MARKETING]: z.boolean(),
    [OVERRIDE_CONTENT_NOT_FOUND_TRANSACTIONAL]: z.boolean(),

    /** Audience Section */
    /** ================ */
    [ENABLE_ANON_USER_TRACKING]: z.boolean(),
    [ENABLE_GEO_IP_LOOKUP]: z.boolean(),
    [ENABLE_EMAIL_CLICK_IP_UPDATE]: z.boolean(),

    /** Insights Section */
    /** ================ */
    [SHOW_REVENUE]: z.boolean(),
    [ENABLE_IMPROVED_TRACKING]: z.boolean(),
    [USE_FILTERED_TRACKING]: z.boolean(),
  })
  .refine(
    ({ ENABLE_SENDING_DOMAIN, sendingDomain = '' }) => {
      /** valid sending domains do not need to have a protocol, but cannot contain handlebars */
      const isValid = isValidDomain(sendingDomain).length < 1;
      return !ENABLE_SENDING_DOMAIN || !!isValid;
    },
    { message: 'Please enter a valid URL.', path: [SENDING_DOMAIN] },
  );

export type ProjectSettingsFormSchemaType = Required<z.infer<typeof schema>>;
export type DirtyFields = FieldNamesMarkedBoolean<ProjectSettingsFormSchemaType>;

export const ProjectSettingsFormProvider: FC<
  React.PropsWithChildren<{ project: Project; labels: ProjectLabel[] }>
> = ({ children, project, labels }) => {
  const defaultValues = useMemo(() => createDefaultValues(project, labels), [project, labels]);

  const methods = useForm<ProjectSettingsFormSchemaType>({
    defaultValues,
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });

  const {
    formState: { submitCount },
    reset,
  } = methods;

  useEffect(() => {
    // reset form when values change before the user submits the form
    if (submitCount < 1) {
      reset(defaultValues);
    }
  }, [defaultValues, reset, submitCount]);

  return <FormProvider {...methods}>{children}</FormProvider>;
};
